import { CashOrThreeMonthEnum, FixingDateEvent, PricePointEnum, WhoFixesDateEnum } from 'api';
import {
  AutocompleteField,
  DateField,
  SelectField,
  type SelectOption,
  TextField,
  useFormContext,
  useWatch,
} from 'form';
import { type I18nTranslateFunction, translate, useTranslation } from 'i18n';
import { useEffect, useState } from 'react';
import { createFilterOptions } from 'ui';

import { OrderFormVariant } from '../../contexts/OrderFormVariant';
import { CreateFormValues } from '../../pages/PurchaseOrderCreate/PurchaseOrderCreate';
import { UpdateFormValues } from '../../pages/PurchaseOrderUpdate/PurchaseOrderUpdate';
import { SalesOrderUpdateFormValues } from '../../pages/SalesOrderUpdate/SalesOrderUpdate';

const INDEX_OPTIONS = [
  {
    label: translate('backlog.variablePricingFields.indexOptions.lmeAluminium', { namespace: 'features' }),
    value: 'LME Aluminium',
  },
  {
    label: translate('backlog.variablePricingFields.indexOptions.lmeAluminiumMidwestPremium', {
      namespace: 'features',
    }),
    value: 'LME Aluminium + Midwest Premium',
  },
];

const getWhoFixesDateOptions = (t: I18nTranslateFunction<string>): SelectOption[] => [
  { label: t('variablePricingFields.whoFixesDateOptions.customer'), value: WhoFixesDateEnum.CUSTOMER },
  { label: t('variablePricingFields.whoFixesDateOptions.safi'), value: WhoFixesDateEnum.SAFI },
  { label: t('variablePricingFields.whoFixesDateOptions.fixedDate'), value: WhoFixesDateEnum.FIXED_DATE },
];

const getFixingDateOptions = (t: I18nTranslateFunction<string>): SelectOption[] => [
  { label: t('variablePricingFields.fixingDateOptions.loading'), value: FixingDateEvent.LOADING },
  { label: t('variablePricingFields.fixingDateOptions.portArrival'), value: FixingDateEvent.PORT_ARRIVAL },
  { label: t('variablePricingFields.fixingDateOptions.siteDelivery'), value: FixingDateEvent.SITE_DELIVERY },
  { label: t('variablePricingFields.fixingDateOptions.billOfLadingIssueDate'), value: FixingDateEvent.BOL_ISSUE_DATE },
  { label: t('variablePricingFields.fixingDateOptions.portOfDeparture'), value: FixingDateEvent.PORT_DEPARTURE },
];

const getCashOr3monthAverageOptions = (t: I18nTranslateFunction<string>): SelectOption[] => [
  { label: t('variablePricingFields.cashOr3MonthAverageOptions.cash'), value: CashOrThreeMonthEnum.CASH },
  {
    label: t('variablePricingFields.cashOr3MonthAverageOptions.threeMonthAverage'),
    value: CashOrThreeMonthEnum.THREE_MONTH_AVERAGE,
  },
  { label: t('variablePricingFields.cashOr3MonthAverageOptions.lowestOfTheTwo'), value: CashOrThreeMonthEnum.LOWEST },
];

const getPricePointOptions = (t: I18nTranslateFunction<string>) => [
  { label: t('variablePricingFields.pricePointOptions.open'), value: PricePointEnum.OPEN },
  { label: t('variablePricingFields.pricePointOptions.close'), value: PricePointEnum.CLOSE },
  { label: t('variablePricingFields.pricePointOptions.low'), value: PricePointEnum.LOW },
  { label: t('variablePricingFields.pricePointOptions.high'), value: PricePointEnum.HIGH },
  { label: t('variablePricingFields.pricePointOptions.volumeWeightedAverage'), value: PricePointEnum.VWAP },
];

type Props = {
  formVariant: OrderFormVariant;
  defaultValues?: SalesOrderUpdateFormValues | UpdateFormValues;
};

export const VariablePricingFields = ({ defaultValues, formVariant }: Props) => {
  const { t } = useTranslation('orders');
  const [marketIndex, setMarketIndex] = useState<SelectOption | null>(INDEX_OPTIONS[0]);
  const whoFixesDateOptions = getWhoFixesDateOptions(t);
  const fixingDateOptions = getFixingDateOptions(t);
  const cashOr3monthAverageOptions = getCashOr3monthAverageOptions(t);
  const pricePointOptions = getPricePointOptions(t);

  const { setValue } = useFormContext<CreateFormValues>();
  const { who_fixes_date: whoFixesDate } = useWatch<CreateFormValues>();

  const filter = createFilterOptions<SelectOption>();

  const defaultValue = defaultValues?.index ? { label: defaultValues.index, value: defaultValues.index } : null;

  // This is a hack to set the initial value for the form as for some reason when defaultValue is passed into Autocomplete it's not set
  useEffect(() => {
    setValue('index', defaultValue?.value ?? (marketIndex?.value as string));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <AutocompleteField<SelectOption, boolean>
        freeSolo
        label={t('variablePricingFields.labels.index')}
        name="index"
        options={INDEX_OPTIONS}
        required
        value={defaultValue ?? marketIndex}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        onSelectOption={(newValue: SelectOption | null) => {
          setMarketIndex(newValue);
        }}
        getOptionLabel={(option: SelectOption | string): string => {
          if (typeof option === 'string') {
            return option;
          }

          if (option.label.startsWith('Use')) {
            return option.value as string;
          }

          return option.label;
        }}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return (
            <li key={key} {...optionProps}>
              {option.label}
            </li>
          );
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);

          const { inputValue } = params;
          const isExisting = options.some((option) => inputValue === option.value);
          if (inputValue !== '' && !isExisting) {
            filtered.push({
              label: t('variablePricingFields.labels.useAsCustomIndex', { values: { inputValue } }),
              value: inputValue,
            });
          }

          return filtered;
        }}
      />
      <TextField
        label={t('variablePricingFields.labels.priceAsPercentageOfIndex')}
        name="price_as_percentage_of_index"
        type="number"
        required
      />

      <SelectField
        label={t('variablePricingFields.labels.cashOrThreeMonthAverage')}
        name="cash_or_three_month"
        options={cashOr3monthAverageOptions}
        required
      />

      <SelectField
        label={t('variablePricingFields.labels.whoFixesTheDate')}
        name="who_fixes_date"
        options={whoFixesDateOptions}
        required
      />

      {whoFixesDate === WhoFixesDateEnum.CUSTOMER || whoFixesDate === WhoFixesDateEnum.SAFI ? (
        <>
          <SelectField
            label={t('variablePricingFields.labels.fixingDateEventStart')}
            name="fixing_date_start_event"
            options={fixingDateOptions}
            required
          />

          <TextField
            label={t('variablePricingFields.labels.fixingDateOffsetStart')}
            name="fixing_date_start_offset"
            type="number"
            required
          />

          <SelectField
            label={t('variablePricingFields.labels.fixingDateEventEnd')}
            name="fixing_date_end_event"
            options={fixingDateOptions}
            required
          />

          <TextField
            label={t('variablePricingFields.labels.fixingDateOffsetEnd')}
            name="fixing_date_end_offset"
            type="number"
            required
          />
        </>
      ) : null}

      {whoFixesDate === WhoFixesDateEnum.FIXED_DATE ? (
        <DateField label={t('variablePricingFields.labels.fixingDate')} name="fixing_date" required />
      ) : null}

      <SelectField
        label={t('variablePricingFields.labels.pricePoint')}
        name="price_point"
        type="number"
        required
        options={pricePointOptions}
      />

      {formVariant === 'update' ? (
        <>
          <TextField type="number" label={t('variablePricingFields.labels.fixedPrice')} name="fixed_price" />
          <DateField
            label={t('variablePricingFields.labels.fixedDate')}
            name="date_fixed"
            pickerTestId="fixed-date-button"
          />
        </>
      ) : null}
    </>
  );
};
